import {Card, FormControl, FormField, Option, Tooltip} from 'platform/components';
import {Grid, Heading, HStack, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {
  BaseSupplierOrderVariantSupplier,
  GetSupplierOrderVariantsResponse,
  SupplierOrderVariantsForServiceOrderWarehouse,
  useLazyGetSupplierOrderTypeQuery,
  useLazyGetSupplierOrderVariantSuppliersQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromCustomersAsSupplier, handleApiError} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {CreateSupplierOrdersFormValues} from './createSupplierOrdersFormSchema';

type SelectableOption = {label?: string | Nullish; value?: string | Nullish};

// The backend may return order types and accounts without a value; those must not be selectable.
const getSelectableOptions = (items: SelectableOption[] | Nullish): Option[] =>
  (items ?? []).flatMap((item) =>
    isNotNil(item.value) && isNotNil(item.label) ? [{label: item.label, value: item.value}] : []
  );

interface WarehouseSupplierOrderCardProps extends RequiredTestIdProps {
  index: number;
  warehouse: SupplierOrderVariantsForServiceOrderWarehouse;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  supplierOrderVariants: GetSupplierOrderVariantsResponse[] | Nullish;
  control: FormControl<CreateSupplierOrdersFormValues>;
  formApi: UseFormReturn<CreateSupplierOrdersFormValues>;
}

export function WarehouseSupplierOrderCard(props: WarehouseSupplierOrderCardProps) {
  const [getVariantSuppliers, {data: variantSuppliers, isFetching: areVariantSuppliersLoading}] =
    useLazyGetSupplierOrderVariantSuppliersQuery();
  const [getSupplierOrderType, {data: supplierOrderType, isFetching: isSupplierOrderTypeLoading}] =
    useLazyGetSupplierOrderTypeQuery();

  const fieldName = `warehouses.${props.index}` as const;

  const [orderVariantId, supplierId, isOrderTypeRequired, isSupplierAccountRequired] = useWatch({
    control: props.control,
    name: [
      `${fieldName}.orderVariantId`,
      `${fieldName}.supplierId`,
      `${fieldName}.isOrderTypeRequired`,
      `${fieldName}.isSupplierAccountRequired`,
    ],
  });

  const orderTypeOptions = isNil(supplierId)
    ? []
    : getSelectableOptions(supplierOrderType?.orderType);
  const supplierAccountOptions = isNil(supplierId)
    ? []
    : getSelectableOptions(supplierOrderType?.supplierAccount);

  const orderVariantOptions = props.warehouse.supplierOrderVariants.map((variant) => ({
    label: variant.name,
    value: variant.supplierOrderVariantId,
  }));
  const hasSingleOrderVariant = orderVariantOptions.length === 1;

  const supplierOptions = getOptionsFromCustomersAsSupplier(
    props.suppliers?.filter((supplier) =>
      variantSuppliers?.some((variantSupplier) => variantSupplier.supplierId === supplier.id)
    )
  );

  // The warehouse default supplier wins when the variant offers it; otherwise the variant default.
  const getDefaultSupplierId = (suppliers: BaseSupplierOrderVariantSupplier[]) =>
    suppliers.find((supplier) => supplier.supplierId === props.warehouse.defaultSupplierId)
      ?.supplierId ?? suppliers.find((supplier) => supplier.isDefault)?.supplierId;

  // A response that arrives after the user picked another variant or supplier must not overwrite the form.
  const isOrderVariantSelected = (requestedOrderVariantId: string) =>
    props.formApi.getValues(`${fieldName}.orderVariantId`) === requestedOrderVariantId;
  const isSupplierSelected = (requestedSupplierId: string) =>
    props.formApi.getValues(`${fieldName}.supplierId`) === requestedSupplierId;

  const loadSupplierOrderTypes = (newOrderVariantId: string, newSupplierId: string) =>
    getSupplierOrderType({supplierOrderVariantId: newOrderVariantId, supplierId: newSupplierId})
      .unwrap()
      .then((response) => {
        if (!isOrderVariantSelected(newOrderVariantId) || !isSupplierSelected(newSupplierId)) {
          return;
        }

        const orderTypes = getSelectableOptions(response.orderType);
        const supplierAccounts = getSelectableOptions(response.supplierAccount);
        const isOrderUseIntegration = props.supplierOrderVariants?.find(
          (variant) => variant.orderVariantId === newOrderVariantId
        )?.isOrderUseIntegration;

        props.formApi.setValue(
          `${fieldName}.isOrderTypeRequired`,
          Boolean(isOrderUseIntegration) && isNotNilOrEmpty(orderTypes)
        );
        props.formApi.setValue(
          `${fieldName}.isSupplierAccountRequired`,
          isNotNilOrEmpty(supplierAccounts)
        );
        // A single account is the only valid choice, so select it for the user.
        props.formApi.setValue(
          `${fieldName}.supplierAccount`,
          supplierAccounts.length === 1 ? String(supplierAccounts[0].value) : null
        );
      })
      .catch(handleApiError);

  const loadOrderVariant = (newOrderVariantId: string) =>
    getVariantSuppliers({orderVariantId: newOrderVariantId})
      .unwrap()
      .then((suppliers) => {
        if (!isOrderVariantSelected(newOrderVariantId)) {
          return;
        }

        const defaultSupplierId = getDefaultSupplierId(suppliers) ?? null;

        props.formApi.setValue(`${fieldName}.supplierId`, defaultSupplierId);

        if (isNotNil(defaultSupplierId)) {
          return loadSupplierOrderTypes(newOrderVariantId, defaultSupplierId);
        }
      })
      .catch(handleApiError);

  useOnMount(() => {
    if (isNotNil(orderVariantId)) {
      loadOrderVariant(orderVariantId);
    }
  });

  const resetSupplierOrderType = () => {
    props.formApi.setValue(`${fieldName}.orderType`, null);
    props.formApi.setValue(`${fieldName}.supplierAccount`, null);
    props.formApi.setValue(`${fieldName}.isOrderTypeRequired`, false);
    props.formApi.setValue(`${fieldName}.isSupplierAccountRequired`, false);
  };

  const handleOrderVariantChange = (value: string | string[] | number | null) => {
    if (!isString(value) || value === orderVariantId) {
      return;
    }

    props.formApi.setValue(`${fieldName}.supplierId`, null);
    resetSupplierOrderType();
    loadOrderVariant(value);
  };

  const handleSupplierChange = (value: string | string[] | number | null) => {
    if (!isString(value) || value === supplierId || isNil(orderVariantId)) {
      return;
    }

    resetSupplierOrderType();
    loadSupplierOrderTypes(orderVariantId, value);
  };

  return (
    <Card variant="inlineGrey">
      <VStack spacing={4}>
        <HStack spacing={3} align="center">
          <FormField
            control={props.control}
            type="checkbox"
            name={`${fieldName}.isSelected`}
            data-testid={suffixTestId('isSelected', props)}
          />
          <Heading size={4}>
            {`${i18n.t('entity.warehouse.labels.warehouse')} - ${props.warehouse.warehouseName}`}
          </Heading>
        </HStack>
        <Grid columns={2}>
          <FormField
            control={props.control}
            type="choice"
            name={`${fieldName}.orderVariantId`}
            label={i18n.t('entity.warehouse.labels.supplierOrderVariant')}
            options={orderVariantOptions}
            onChange={handleOrderVariantChange}
            isDisabled={hasSingleOrderVariant}
            isRequired
            isNotClearable
            menuInPortal
            data-testid={suffixTestId('orderVariantId', props)}
          />
          <Tooltip
            isDisabled={isNotNil(orderVariantId)}
            label={i18n.t('entity.warehouse.labels.firstlySelectSupplierOrderVariant')}
          >
            <FormField
              control={props.control}
              type="choice"
              name={`${fieldName}.supplierId`}
              label={i18n.t('entity.warehouse.labels.supplier')}
              options={supplierOptions}
              onChange={handleSupplierChange}
              isDisabled={isNil(orderVariantId)}
              isLoading={areVariantSuppliersLoading}
              isRequired
              isNotClearable
              menuInPortal
              data-testid={suffixTestId('supplierId', props)}
            />
          </Tooltip>
          <Tooltip
            isDisabled={isNotNil(supplierId)}
            label={i18n.t('entity.warehouse.labels.firstlySelectSupplierOrderVariantAndSupplier')}
          >
            <FormField
              control={props.control}
              type="choice"
              name={`${fieldName}.orderType`}
              label={i18n.t('entity.warehouse.labels.supplierOrderType')}
              options={orderTypeOptions}
              isDisabled={isNil(supplierId)}
              isLoading={isSupplierOrderTypeLoading}
              isRequired={isOrderTypeRequired}
              isNotClearable
              menuInPortal
              data-testid={suffixTestId('orderType', props)}
            />
          </Tooltip>
          <Tooltip
            isDisabled={isNotNil(supplierId)}
            label={i18n.t('entity.warehouse.labels.firstlySelectSupplierOrderVariantAndSupplier')}
          >
            <FormField
              control={props.control}
              type="choice"
              name={`${fieldName}.supplierAccount`}
              label={i18n.t('entity.warehouse.labels.supplierAccount')}
              options={supplierAccountOptions}
              isDisabled={isNil(supplierId) || supplierAccountOptions.length <= 1}
              isLoading={isSupplierOrderTypeLoading}
              isRequired={isSupplierAccountRequired}
              isNotClearable
              menuInPortal
              data-testid={suffixTestId('supplierAccount', props)}
            />
          </Tooltip>
        </Grid>
      </VStack>
    </Card>
  );
}
